<script setup lang="ts">
/**
 * Demande de remplacement de matériel (modale) — module Onboarding V2 (cycle M7).
 *
 * Ouvre un ticket de remplacement pour un objet existant : motif (adapté au type
 * d'équipement), source de la demande, commande externe, échéance de rappel et
 * notes. À la validation, délègue au store (`requestReplacement`) qui crée le
 * nouvel objet chaîné + le ticket, puis émet `created` avec l'id du ticket
 * (le parent s'en sert pour naviguer).
 *
 * Deux modes d'ouverture :
 *  - **pré-sélectionné** — `open(userUuid, asset)` depuis la fiche collaborateur :
 *    l'objet est imposé, on n'affiche pas les sélecteurs.
 *  - **complet** — `open()` depuis « Nouvelle demande » de la liste : on choisit
 *    le collaborateur puis l'équipement dans la modale.
 *
 * Pattern modale natif `<dialog class="modal">` DaisyUI — cf. OnbHandoverDialog.vue.
 * Le parent récupère un `ref` et appelle `open(...)` (exposé via defineExpose).
 */
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import {
  REPLACEMENT_SOURCE_META,
  isReplacementActive,
  reasonsForAsset,
} from '@/v2/onboarding/catalog'
import type {
  AssetAssignment,
  CollaboratorView,
  OwnedAsset,
  ReplacementSource,
} from '@/v2/onboarding/types'

const emit = defineEmits<{
  created: [ticketId: string]
}>()

const store = useOnboardingStore()
const { collaborators } = storeToRefs(store)

const dialog = ref<HTMLDialogElement | null>(null)

/* Cible (collaborateur + objet) du remplacement. */
/** UUID du collaborateur ciblé (clé d'API et value du sélecteur). */
const userUuid = ref('')
const selectedAsset = ref<AssetAssignment | null>(null)
/** true → objet imposé (mode pré-sélectionné) : pas de sélecteurs. */
const preselected = ref(false)

/* Champs du formulaire. */
const reason = ref('')
/** Précision libre lorsque le motif « Autre » est sélectionné. */
const otherReason = ref('')
const source = ref<ReplacementSource | ''>('')
const externalOrder = ref(false)
/** Échéance / rappel au format YYYY-MM-DD (input date), '' si non renseigné. */
const reminderAt = ref('')
const notes = ref('')

const titleId = 'onb-replacement-dialog-title'

/** Liste des sources (Email / Teams / Ticket / Autre) pour le sélecteur. */
const sourceOptions = computed(
  () =>
    Object.entries(REPLACEMENT_SOURCE_META) as [ReplacementSource, { label: string }][],
)

/** Collaborateur ciblé (résolu par userUuid dans la vue agrégée du store). */
const selectedCollaborator = computed<CollaboratorView | null>(
  () => collaborators.value.find((c) => c.userUuid === userUuid.value) ?? null,
)

/** Inventaire remplaçable du collaborateur sélectionné (mode complet). */
const ownedAssets = computed<OwnedAsset[]>(
  () => selectedCollaborator.value?.ownedAssets ?? [],
)

/** N° de série / IMEI affichable d'un objet. */
function serialOrImei(asset: AssetAssignment): string {
  return [asset.attributes.serialNumber, asset.attributes.imei]
    .filter((v): v is string => !!v)
    .join(' / ')
}

/** N° de série / IMEI de l'objet ciblé (encart pré-sélectionné). */
const selectedSerialOrImei = computed(() =>
  selectedAsset.value ? serialOrImei(selectedAsset.value) : '',
)

/** Un objet a-t-il déjà un ticket de remplacement actif (à désactiver) ? */
function hasActiveReplacement(asset: AssetAssignment): boolean {
  return (
    selectedCollaborator.value?.tickets.some(
      (t) => t.oldAssignmentId === asset.id && isReplacementActive(t.status),
    ) ?? false
  )
}

/** Motifs proposés selon la famille de l'objet ciblé (vide tant qu'aucun objet). */
const reasonOptions = computed(() =>
  selectedAsset.value ? reasonsForAsset(selectedAsset.value.category) : [],
)

/** Indique si le motif « Autre » est sélectionné (affiche le champ « Préciser »). */
const isOther = computed(() => reason.value === 'Autre')

/**
 * Motif final transmis au store : la précision saisie si « Autre » est choisi
 * (sinon « Autre »), ou le motif brut.
 */
const finalReason = computed(() => {
  if (isOther.value) {
    const detail = otherReason.value.trim()
    return detail || 'Autre'
  }
  return reason.value
})

/** Création possible : collaborateur + équipement + motif + source renseignés. */
const canCreate = computed(
  () => !!userUuid.value && !!selectedAsset.value && reason.value !== '' && source.value !== '',
)

/** Réinitialise tous les champs du formulaire. */
function resetForm(): void {
  reason.value = ''
  otherReason.value = ''
  source.value = ''
  externalOrder.value = false
  reminderAt.value = ''
  notes.value = ''
}

/**
 * Ouvre la modale (API publique consommée par le parent).
 * - `open(userUuid, asset)` → mode pré-sélectionné (objet imposé).
 * - `open()` → mode complet (sélection collaborateur + équipement dans la modale).
 */
function open(userUuidValue?: string, target?: AssetAssignment): void {
  resetForm()
  if (target) {
    preselected.value = true
    userUuid.value = userUuidValue ?? ''
    selectedAsset.value = target
  } else {
    preselected.value = false
    userUuid.value = ''
    selectedAsset.value = null
  }
  dialog.value?.showModal()
}

function close(): void {
  dialog.value?.close()
}

defineExpose({ open })

/** Changement de collaborateur (mode complet) : réinitialise l'objet et le motif. */
function onCollaboratorChange(): void {
  selectedAsset.value = null
  reason.value = ''
  otherReason.value = ''
}

/** Sélection d'un objet (mode complet) par id : réinitialise le motif. */
function onAssetChange(assetId: string): void {
  selectedAsset.value = ownedAssets.value.find((a) => a.id === assetId) ?? null
  reason.value = ''
  otherReason.value = ''
}

async function submit(): Promise<void> {
  if (!canCreate.value || !selectedAsset.value || source.value === '') return
  const id = await store.requestReplacement({
    userUuid: userUuid.value,
    oldAssignmentId: selectedAsset.value.id,
    reason: finalReason.value,
    source: source.value,
    externalOrder: externalOrder.value,
    reminderAt: reminderAt.value || null,
    notes: notes.value.trim() || undefined,
  })
  if (id) {
    emit('created', id)
    close()
  }
}
</script>

<template>
  <dialog ref="dialog" class="modal" :aria-labelledby="titleId">
    <div class="modal-box w-full max-w-lg max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
      <!-- En-tête + fermeture -->
      <div class="flex items-start justify-between gap-4">
        <h2 :id="titleId" class="text-lg font-semibold">Demander un remplacement</h2>
        <button
          type="button"
          class="btn btn-ghost btn-sm motion-reduce:transition-none"
          aria-label="Fermer"
          @click="close"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <!-- Formulaire -->
      <form class="mt-4 space-y-4" @submit.prevent="submit">
        <!-- Mode complet : sélection du collaborateur -->
        <div v-if="!preselected">
          <label class="label" for="onb-replacement-collaborator">
            <span class="label-text">Collaborateur</span>
          </label>
          <select
            id="onb-replacement-collaborator"
            v-model="userUuid"
            class="select select-bordered w-full"
            required
            @change="onCollaboratorChange"
          >
            <option value="" disabled>Sélectionner un collaborateur</option>
            <option
              v-for="c in collaborators"
              :key="c.email"
              :value="c.userUuid"
              :disabled="!c.userUuid"
            >
              {{ c.firstName }} {{ c.lastName }} — {{ c.entity }}
            </option>
          </select>
          <p v-if="selectedCollaborator?.email" class="mt-1 text-sm text-base-content/80">
            {{ selectedCollaborator.email }}
          </p>
        </div>

        <!-- Mode complet : sélection de l'équipement -->
        <div v-if="!preselected && userUuid">
          <label class="label" for="onb-replacement-asset">
            <span class="label-text">Équipement à remplacer</span>
          </label>
          <p v-if="!ownedAssets.length" class="text-base text-base-content/80">
            Ce collaborateur ne détient aucun équipement remplaçable.
          </p>
          <select
            v-else
            id="onb-replacement-asset"
            class="select select-bordered w-full"
            required
            :value="selectedAsset?.id ?? ''"
            @change="onAssetChange(($event.target as HTMLSelectElement).value)"
          >
            <option value="" disabled>Sélectionner un équipement</option>
            <option
              v-for="a in ownedAssets"
              :key="a.id"
              :value="a.id"
              :disabled="hasActiveReplacement(a)"
            >
              {{ a.label }}<template v-if="serialOrImei(a)"> · {{ serialOrImei(a) }}</template
              ><template v-if="hasActiveReplacement(a)"> — remplacement en cours</template>
            </option>
          </select>
        </div>

        <!-- Mode pré-sélectionné : objet ciblé en lecture seule -->
        <div
          v-if="preselected && selectedAsset"
          class="rounded-box border border-base-300 bg-base-200 p-3"
        >
          <p class="text-sm text-base-content/80">Équipement à remplacer</p>
          <p class="mt-1 text-base font-medium break-words text-base-content">
            {{ selectedAsset.label }}
            <span v-if="selectedSerialOrImei" class="tabular-nums break-all text-base-content/80">
              · {{ selectedSerialOrImei }}
            </span>
          </p>
        </div>

        <!-- Motif (options dépendantes du type d'équipement) -->
        <div v-if="selectedAsset">
          <label class="label" for="onb-replacement-reason">
            <span class="label-text">Motif du remplacement</span>
          </label>
          <select
            id="onb-replacement-reason"
            v-model="reason"
            class="select select-bordered w-full"
            required
          >
            <option value="" disabled>Sélectionner un motif</option>
            <option v-for="r in reasonOptions" :key="r" :value="r">{{ r }}</option>
          </select>
        </div>

        <!-- Précision si « Autre » -->
        <div v-if="selectedAsset && isOther">
          <label class="label" for="onb-replacement-other">
            <span class="label-text">Préciser le motif</span>
          </label>
          <input
            id="onb-replacement-other"
            v-model="otherReason"
            type="text"
            class="input input-bordered w-full"
          />
        </div>

        <!-- Source de la demande -->
        <div>
          <label class="label" for="onb-replacement-source">
            <span class="label-text">Source de la demande</span>
          </label>
          <select
            id="onb-replacement-source"
            v-model="source"
            class="select select-bordered w-full"
            required
          >
            <option value="" disabled>Sélectionner une source</option>
            <option v-for="[value, meta] in sourceOptions" :key="value" :value="value">
              {{ meta.label }}
            </option>
          </select>
        </div>

        <!-- Commande externe -->
        <div class="form-control">
          <label class="label cursor-pointer justify-start gap-3" for="onb-replacement-external">
            <input
              id="onb-replacement-external"
              v-model="externalOrder"
              type="checkbox"
              class="checkbox"
            />
            <span class="label-text">Commande externe (délai fournisseur)</span>
          </label>
        </div>

        <!-- Échéance / rappel -->
        <div>
          <label class="label" for="onb-replacement-reminder">
            <span class="label-text">Date de rappel / échéance (optionnel)</span>
          </label>
          <input
            id="onb-replacement-reminder"
            v-model="reminderAt"
            type="date"
            class="input input-bordered w-full tabular-nums"
          />
        </div>

        <!-- Notes -->
        <div>
          <label class="label" for="onb-replacement-notes">
            <span class="label-text">Notes (optionnel)</span>
          </label>
          <textarea
            id="onb-replacement-notes"
            v-model="notes"
            class="textarea textarea-bordered w-full"
            rows="3"
          ></textarea>
        </div>

        <!-- Actions -->
        <div class="modal-action flex-wrap">
          <button type="button" class="btn btn-ghost motion-reduce:transition-none" @click="close">
            Annuler
          </button>
          <button
            type="submit"
            class="btn btn-primary motion-reduce:transition-none"
            :disabled="!canCreate"
          >
            Créer la demande
          </button>
        </div>
      </form>
    </div>

    <form method="dialog" class="modal-backdrop">
      <button aria-label="Fermer">Fermer</button>
    </form>
  </dialog>
</template>
